import React from 'react';
import { motion } from 'framer-motion';
import { MapPin } from 'lucide-react';
import { Kicker, H2, CtaButton, Reveal, ProtectedImage, SERIF, SCROLL_MT } from './shared';

const TILT = ['-1.5deg', '2deg', '-1.2deg'];

function Evidence({ item, index, protection, className = '' }) {
  return (
    <motion.figure initial={{ opacity: 0, y: 30, rotate: 0 }} whileInView={{ opacity: 1, y: 0, rotate: TILT[index % 3] }} viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.7, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
      className={`relative ${className}`} data-testid={`proof-item-${index}`}>
      <span className="absolute -top-3 left-1/2 -translate-x-1/2 w-24 h-6 bg-amber-200/70 rotate-[-2deg] rounded-sm shadow-sm z-10" aria-hidden="true" />
      <div className="rounded-2xl bg-white p-2 sm:p-2.5 shadow-[0_28px_60px_-28px_rgba(15,23,42,0.45)] border border-neutral-200 select-none">
        <ProtectedImage src={item.image_url} alt={item.title || 'Proof'} protection={protection} className="rounded-xl bg-neutral-100" imgClassName="w-full h-auto max-h-[440px] object-contain block">
          <span className="absolute top-2.5 left-2.5 w-7 h-7 rounded-full bg-slate-900 text-white text-[12px] font-extrabold flex items-center justify-center shadow" style={SERIF}>{index + 1}</span>
        </ProtectedImage>
        <figcaption className="px-2 pt-2.5 pb-1">
          <p className="text-[13.5px] font-bold text-slate-900 leading-snug">{item.title}</p>
          {item.caption && <p className="text-[12px] text-amber-700 font-semibold mt-0.5 leading-snug">{item.caption}</p>}
        </figcaption>
      </div>
    </motion.figure>
  );
}

export default function ProofBoard({ teacher, onCta, protection }) {
  const proofs = (teacher.proofs || []).filter(p => p && p.image_url).slice(0, 3);
  const badges = (teacher.badges || []).filter(Boolean);
  const prot = protection?.on_proofs === false ? { enabled: false } : protection;

  return (
    <section id="proof" className={`${SCROLL_MT} py-12 sm:py-16 lg:py-20`} data-testid="proof-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <Reveal className="text-center max-w-3xl mx-auto">
          <Kicker>{teacher.label}</Kicker>
          <h2 className={H2} data-testid="teacher-title">{teacher.title}</h2>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2.5">
            {teacher.image_url && <img src={teacher.image_url} alt={teacher.name || 'Your teacher'} className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow" data-testid="teacher-photo" />}
            {teacher.name && <p className="text-amber-700 font-bold text-[15px]" style={SERIF} data-testid="teacher-name">— {teacher.name}</p>}
          </div>
          {badges.length > 0 && (
            <div className="mt-3.5 flex flex-wrap justify-center gap-2">
              {badges.map((b, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-slate-900 text-white text-[12.5px] font-semibold" data-testid={`teacher-badge-${i}`}>
                  {i === 0 && !/^\p{Extended_Pictographic}/u.test(b) && <MapPin size={12} className="text-amber-400" />}{b}
                </span>
              ))}
            </div>
          )}
          {teacher.proof_intro && <p className="mt-6 text-xl sm:text-2xl text-slate-800 leading-snug" style={SERIF} data-testid="teacher-proof-intro">{teacher.proof_intro}</p>}
          {teacher.bio && <p className="mt-4 text-[15px] sm:text-base text-slate-600 leading-relaxed whitespace-pre-line" data-testid="teacher-bio">{teacher.bio}</p>}
        </Reveal>

        {proofs.length > 0 && (
          <div className="relative mt-10 sm:mt-12 max-w-5xl mx-auto" data-testid="proof-board">
            <div className="pointer-events-none absolute -inset-4 sm:-inset-8 rounded-[36px] bg-white/50 border border-white/70" aria-hidden="true" />
            <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-7 sm:gap-6 items-center pt-3" data-testid="proof-grid">
              {proofs.map((p, i) => (
                <Evidence key={i} item={p} index={i} protection={prot} className={i === 1 ? 'z-10' : ''} />
              ))}
            </div>
            {teacher.proof_footnote && <p className="relative mt-7 text-[12px] text-slate-500 text-center" data-testid="proof-footnote">{teacher.proof_footnote}</p>}
          </div>
        )}
        {teacher.cta_label && <div className="mt-8 text-center"><CtaButton label={teacher.cta_label} onClick={() => onCta('teacher')} variant="dark" size="md" testId="teacher-cta" /></div>}
      </div>
    </section>
  );
}
